import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CardType } from '../../core/enums/card-type.enum';

import { NewCard } from '../../core/models/new-card.model';

import { ModalService } from '../../core/services/modal.service';
import { SettingsService } from '../../core/services/settings.service';
import { WindowMessagesAllowedOrigins, WindowMessagesService } from '../../core/services/window-messages.service';

export const CreditCardModalPageIdentifier = 'credit-card-modal';

interface TapV3ReadyEvent {
  type: 'ready';
  payload: undefined;
}

interface TapV3ValidationEvent {
  type: 'validation';
  payload:
    | { loaded: boolean }
    | { layout: { height: number } }
    | { code: number; focus: 'in' | 'out' }
    | { code: number; status: string }
    | { error_interactive: { code: number; message: string; key: string } }
    | {
        error_interactive: { code: number; message: string; key: string };
        BIN: {
          bin: string;
          bank: string;
          card_brand: string;
          card_type: string;
          card_category: string;
          card_scheme: string;
          country: string;
          address_required: boolean;
          api_version: string;
          issuer_id: string;
          brand: string;
        };
      }
    | { valid: { code: number; key: string } }
    | {
        valid: { code: number; key: string };
        BIN: {
          bin: string;
          bank: string;
          card_brand: string;
          card_type: string;
          card_category: string;
          card_scheme: string;
          country: string;
          address_required: boolean;
          api_version: string;
          issuer_id: string;
          brand: string;
        };
      }
    | {
        code: number;
        status: string;
        error: string;
        valid: { code: number; key: string };
        BIN: {
          bin: string;
          bank: string;
          card_brand: string;
          card_type: string;
          card_category: string;
          card_scheme: string;
          country: string;
          address_required: boolean;
          api_version: string;
          issuer_id: string;
          brand: string;
        };
      }
    | {
        type: 'token';
        data: {
          success: boolean;
          result: {
            id: string;
            created: number;
            object: 'token';
            live_mode: boolean;
            type: string;
            used: boolean;
            card: {
              id: string;
              object: string;
              address: Record<string, unknown>;
              funding: string;
              fingerprint: string;
              brand: CardType;
              scheme: string;
              name: string;
              exp_month: number;
              exp_year: number;
              last_four: string;
              first_six: string;
            };
          };
        };
      };
}

interface TapV3TokenEvent {
  type: 'token';
  payload: {
    token: string;
    error?: {
      message: string;
    };
  };
}

type TapV3Event = TapV3ReadyEvent | TapV3ValidationEvent | TapV3TokenEvent;

@Component({
  selector: 'app-credit-card-modal',
  templateUrl: './credit-card-modal.page.html',
  styleUrls: ['./credit-card-modal.page.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CreditCardModalPage implements OnInit, OnDestroy {
  @ViewChild('cardFrame')
  public cardFrame: ElementRef<HTMLIFrameElement>;

  public isLoading = false;
  public cardError: string;
  public tapSettingsPublicKey: string;
  public tapSettingsMerchantId: string;
  public saveCard = true;

  private cardDetails: {
    id: string;
    object: string;
    address: Record<string, unknown>;
    funding: string;
    fingerprint: string;
    brand: CardType;
    scheme: string;
    name: string;
    exp_month: number;
    exp_year: number;
    last_four: string;
    first_six: string;
    token: string;
  };

  private missingFieldKeys = [
    'card_number_required',
    'expiry_date_required',
    'expiry_year_required',
    'expiry_month_required',
    'cvv_digit_required',
    'card_holder_required',
  ];

  private unsubscribe$ = new Subject<void>();

  constructor(
    private modalService: ModalService,
    private settingsService: SettingsService,
    private windowMessagesService: WindowMessagesService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.tapSettingsPublicKey = this.settingsService.getTapSettingsPublicKey();
    this.tapSettingsMerchantId = this.settingsService.getTapSettingsMerchantId();

    this.windowMessagesService
      .onMessages({ fromOrigin: WindowMessagesAllowedOrigins.PaymentPage })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((event: TapV3Event) => {
        switch (event.type) {
          case 'ready':
            this.handleReadyEvent();
            break;

          case 'validation':
            this.handleValidationEvent(event);
            break;

          case 'token':
            this.handleTokenEvent(event);
            break;
        }
      });

    this.cardError = this.translateService.instant('CARD_PAYMENT.DETAILS_REQUIRED') as string;
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public onSubmit(): void {
    if (this.cardError) {
      return;
    }

    this.isLoading = true;
    this.cardFrame?.nativeElement?.contentWindow.postMessage({ type: 'submit' }, '*');
  }

  public onDismiss(cardDetails?: NewCard): Promise<boolean> {
    return this.modalService.dismissModal({ id: CreditCardModalPageIdentifier, data: cardDetails });
  }

  private handleReadyEvent(): void {
    if (this.cardFrame?.nativeElement?.contentWindow) {
      this.windowMessagesService.sendMessage({
        type: 'init',
        payload: {
          publicKey: this.tapSettingsPublicKey,
          merchantId: this.tapSettingsMerchantId,
          paymentOptions: {
            paymentAllowed: 'all',
            currencyCode: [this.settingsService.getCurrentIsoCurrencyCode()],
            labels: {
              cardNumber: this.translateService.instant('FIELDS.CREDIT_CARD_NUMBER') as string,
              expirationDate: this.translateService.instant('CARD_PAYMENT.EXPIRATION_MASK') as string,
              cvv: this.translateService.instant('CARD_PAYMENT.CVV') as string,
              cardHolder: this.translateService.instant('CARD_PAYMENT.HOLDER_NAME') as string,
            },
            TextDirection: this.settingsService.getLanguageDirection(),
          },
        },
        targetWindow: this.cardFrame.nativeElement.contentWindow,
        targetOrigin: WindowMessagesAllowedOrigins.PaymentPage,
      });
    }
  }

  private handleValidationEvent(event: TapV3ValidationEvent): void {
    const { payload } = event;

    if ('type' in payload && payload.type === 'token' && 'data' in payload && 'success' in payload.data && payload.data.success === true) {
      // We keep both the card details AND the token because this is a
      // 'validation' event — once we get the token from the 'token' event
      // we'll check if the token is the same to confirm that the credit
      // card details were not changed
      this.cardDetails = {
        token: payload.data.result.id,
        ...payload.data.result.card,
      };
    }

    if ('status' in payload && payload.status === 'success') {
      this.cardError = '';
    } else if ('error_interactive' in payload) {
      if (this.missingFieldKeys.some((k) => k === payload.error_interactive.key)) {
        this.cardError = this.translateService.instant('CARD_PAYMENT.DETAILS_REQUIRED') as string;
      } else if (payload.error_interactive.key === 'error_invalid_card') {
        this.cardError = this.translateService.instant('CARD_PAYMENT.INVALID_CARD') as string;
      } else if (payload.error_interactive.key === 'error_card_holder_short') {
        this.cardError = this.translateService.instant('CARD_PAYMENT.HOLDER_NAME_TOO_SHORT') as string;
      } else if (payload.error_interactive.key === 'error_invalid_expiry_characters') {
        this.cardError = this.translateService.instant('CARD_PAYMENT.INVALID_EXPIRY_DATE') as string;
      } else if (payload.error_interactive.key === 'error_invalid_cvv_characters') {
        this.cardError = this.translateService.instant('CARD_PAYMENT.INVALID_CARD_CVV') as string;
      }
    }
  }

  private handleTokenEvent(event: TapV3TokenEvent): void {
    this.isLoading = false;
    const { token, error } = event.payload;

    if (error) {
      this.cardError = error.message;
      return;
    }

    // For some reason the token we received is different than the token we
    // associated with the card, so maybe the card was changed and TAP didn't
    // fire the validation event with the new card details
    if (this.cardDetails && this.cardDetails.token !== token) {
      this.cardDetails = undefined;
    }

    const cardDetails: NewCard = {
      isNew: true,
      cardToken: token,
      shouldSave: this.saveCard,
      cardId: this.cardDetails ? this.cardDetails.id : token,
      cardHolderName: this.cardDetails ? this.cardDetails.name : undefined,
      cardType: this.cardDetails ? this.cardDetails.brand : undefined,
      expiryMonth: this.cardDetails ? this.cardDetails.exp_month : undefined,
      expiryYear: this.cardDetails ? this.cardDetails.exp_year : undefined,
      firstSixDigits: this.cardDetails ? this.cardDetails.first_six : undefined,
      lastFourDigits: this.cardDetails ? this.cardDetails.last_four : undefined,
    };

    void this.onDismiss(cardDetails);
  }
}
